import { memo } from "react";
import ReactECharts from "echarts-for-react";

function ConceptChart({ data, concept, isQuarterly, height = "400px", maxPeriods = null }) {
  const conceptData = data.filter(item => item.concept === concept);

  const timePeriods = [];
  const values = [];

  conceptData.forEach(item => {
    let period;
    if (isQuarterly) {
      period = item.fp ? `${item.fy} ${item.fp}` : item.fy;
    } else {
      period = item.fy;
    }

    if (!timePeriods.includes(period)) {
      timePeriods.push(period);
      values.push(item.value !== null && item.value !== undefined ? item.value : 0);
    }
  });

  timePeriods.sort((a, b) => {
    if (isQuarterly) {
      const [yearA, periodA] = a.split(' ');
      const [yearB, periodB] = b.split(' ');
      if (yearA !== yearB) {
        return parseInt(yearA) - parseInt(yearB);
      }
      const periodOrder = { 'Q1': 1, 'Q2': 2, 'Q3': 3, 'Q4': 4 };
      return (periodOrder[periodA] || 0) - (periodOrder[periodB] || 0);
    } else {
      return parseInt(a) - parseInt(b);
    }
  });

  // If maxPeriods is set, keep only the most recent N periods (tail of the sorted array)
  if (maxPeriods && timePeriods.length > maxPeriods) {
    timePeriods.splice(0, timePeriods.length - maxPeriods);
  }

  const sortedValues = timePeriods.map(period => {
    const item = conceptData.find(d => {
      if (isQuarterly) {
        const periodKey = d.fp ? `${d.fy} ${d.fp}` : d.fy;
        return periodKey === period;
      } else {
        return d.fy === period;
      }
    });
    return item && item.value !== null && item.value !== undefined ? item.value : 0;
  });

  const n = sortedValues.length;
  const sumX = timePeriods.reduce((sum, _, i) => sum + i, 0);
  const sumY = sortedValues.reduce((sum, val) => sum + val, 0);
  const sumXY = timePeriods.reduce((sum, _, i) => sum + i * sortedValues[i], 0);
  const sumX2 = timePeriods.reduce((sum, _, i) => sum + i * i, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const trendData = timePeriods.map((_, i) => slope * i + intercept);

  const firstItem = conceptData[0];
  const shouldFormatMillions = !firstItem?.concept?.toLowerCase().includes('earnings') &&
    !firstItem?.concept?.toLowerCase().includes('per share');

  const formatValue = (val) => {
    if (val === null || val === undefined || isNaN(val)) return 0;
    if (shouldFormatMillions) {
      return (val / 1000000).toFixed(2);
    }
    return val.toFixed(2);
  };

  const option = {
    backgroundColor: "transparent",
    textStyle: { color: "#e5e7eb" },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      formatter: (params) => {
        let result = `${params[0].name}<br/>`;
        params.forEach(param => {
          const value = shouldFormatMillions
            ? `$${formatValue(param.value)}M`
            : formatValue(param.value);
          result += `${param.seriesName}: ${value}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: ["Value", "Trend"],
      textStyle: { color: "#e5e7eb" }
    },
    grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
    xAxis: {
      type: "category",
      data: timePeriods,
      axisLine: { lineStyle: { color: "#6b7280" } },
      axisLabel: {
        color: "#9ca3af",
        rotate: timePeriods.length > 10 ? 45 : 0
      }
    },
    yAxis: {
      type: "value",
      axisLine: { lineStyle: { color: "#6b7280" } },
      axisLabel: {
        color: "#9ca3af",
        formatter: (value) => {
          if (shouldFormatMillions) {
            return `$${(value / 1000000).toFixed(0)}M`;
          }
          return value.toFixed(0);
        }
      },
      splitLine: { lineStyle: { color: "#374151" } }
    },
    series: [
      {
        name: "Value",
        type: "bar",
        data: sortedValues,
        itemStyle: { color: "#3b82f6" },
        emphasis: { itemStyle: { color: "#2563eb" } }
      },
      {
        name: "Trend",
        type: "line",
        data: trendData,
        smooth: true,
        itemStyle: { color: "#10b981" },
        lineStyle: { color: "#10b981", width: 2 },
        symbol: "none"
      }
    ]
  };

  if (sortedValues.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No data available for this concept</p>
      </div>
    );
  }

  return <ReactECharts option={option} style={{ height, width: "100%" }} />;
}

export default memo(ConceptChart);
